import React from "react";
import { StyleSheet, View } from "@react-pdf/renderer";
import { get } from "lodash-es";
import { CONTRACT_INFO_STEP2_FIELD as FIELD } from "config/documentConstant";
import DocumentDetail from "../../common/DocumentDetail";
import ContractInfoProfitabilityTable from "./ContractInfoProfitabilityTable";
import {
  ContractInfoBorderBox,
  ContractInfoDivider,
  ContractInfoInlineField,
  ContractInfoRow,
  ContractInfoSection,
  ContractInfoValueText,
  formatContractInfoValue,
} from "./ContractInfoPdfLayout";

interface ContractInfoReviewResultSectionProps {
  documentData: Record<string, unknown>;
}

const LABEL_WIDTH = "17mm";

const styles = StyleSheet.create({
  reviewBody: {
    display: "flex",
    flexDirection: "column",
    gap: "1mm",
  },
  pairRow: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    gap: "4mm",
    paddingTop: "0.6mm",
    paddingBottom: "0.6mm",
    borderBottom: "0.2mm solid #C6C8CD",
  },
  pairRowLast: {
    borderBottom: "0mm solid transparent",
  },
  pairLeft: {
    width: "48%",
    display: "flex",
    flexDirection: "row",
    alignSelf: "stretch",
  },
  pairRight: {
    width: "52%",
    display: "flex",
    flexDirection: "row",
    alignSelf: "stretch",
  },
  processingList: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "4.8mm",
  },
  shortReviewResultRow: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    gap: "4mm",
    minHeight: "16mm",
    paddingTop: "0.8mm",
    paddingBottom: "0.8mm",
  },
  reviewResultLabelWrap: {
    width: LABEL_WIDTH,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignSelf: "stretch",
  },
  shortReviewResultContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minHeight: "16mm",
  },
  longReviewResultList: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  longReviewResultLineRow: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
  },
  longReviewResultLabelCell: {
    width: LABEL_WIDTH,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  longReviewResultLineContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingLeft: "4mm",
    paddingTop: "0.4mm",
    paddingBottom: "0.4mm",
  },
  reviewResultText: {
    width: "100%",
  },
  shortReviewResultText: {
    width: "100%",
  },
});

const ContractInfoReviewResultSection = ({
  documentData,
}: ContractInfoReviewResultSectionProps) => {
  const location = get(documentData, FIELD.BASIC_REVIEW.fields.LOCATION.key);
  const businessRightAcquisition = get(
    documentData,
    FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION.key,
  );
  const permission = get(
    documentData,
    FIELD.BASIC_REVIEW.fields.PERMISSION.key,
  );
  const marketStatus = get(
    documentData,
    FIELD.BASIC_REVIEW.fields.MARKET_STATUS.key,
  );
  const appropriateSalePrice = get(
    documentData,
    FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.key,
  );
  const reviewResult = get(documentData, FIELD.REVIEW_RESULT.key);
  const processingResult = get(documentData, FIELD.PROCESSING_RESULT.key);
  const formattedReviewResult = formatContractInfoValue(reviewResult);
  const reviewResultLines =
    typeof formattedReviewResult === "string"
      ? formattedReviewResult.split("\n")
      : [String(formattedReviewResult)];
  // '검토 결과' 라벨을 전체 본문 높이의 1/2 지점에 배치.
  // react-pdf는 렌더 전 줄바꿈(wrap) 수를 알려주지 않으므로, 각 논리 줄이
  // 차지할 시각 줄 수를 글자 수로 추정(CHARS_PER_LINE)해 가중한 뒤 절반 지점을 찾는다.
  const CHARS_PER_LINE = 50;
  const lineHeights = reviewResultLines.map((line) =>
    Math.max(1, Math.ceil((line?.length || 1) / CHARS_PER_LINE)),
  );
  const totalHeight = lineHeights.reduce((sum, h) => sum + h, 0);
  let accumulatedHeight = 0;
  let reviewLabelLineIndex = 0;
  for (let i = 0; i < lineHeights.length; i += 1) {
    accumulatedHeight += lineHeights[i];
    if (accumulatedHeight >= totalHeight / 2) {
      reviewLabelLineIndex = i;
      break;
    }
  }
  const isShortReviewResult =
    typeof reviewResult === "string" &&
    reviewResult.trim().length > 0 &&
    reviewResult.trim().length <= 80 &&
    !reviewResult.includes("\n");

  return (
    <ContractInfoSection title="2. 검토 결과">
      <View style={styles.reviewBody}>
        <ContractInfoBorderBox>
          {/* 행1: 사업권원확보(좌) | 인허가(우) */}
          <View style={[styles.pairRow, { minHeight: "6mm" }]}>
            <View style={styles.pairLeft}>
              <ContractInfoInlineField
                label="사업권원확보"
                labelWidth={LABEL_WIDTH}
              >
                <ContractInfoValueText>
                  {formatContractInfoValue(businessRightAcquisition)}
                </ContractInfoValueText>
              </ContractInfoInlineField>
            </View>
            <View style={styles.pairRight}>
              <ContractInfoInlineField label="인허가" labelWidth={LABEL_WIDTH}>
                <ContractInfoValueText>
                  {formatContractInfoValue(permission)}
                </ContractInfoValueText>
              </ContractInfoInlineField>
            </View>
          </View>

          {/* 행2: 입지(좌) | 적정분양가(우) */}
          <View style={[styles.pairRow, { minHeight: "19mm" }]}>
            <View style={styles.pairLeft}>
              <ContractInfoInlineField label="입지" labelWidth={LABEL_WIDTH}>
                <ContractInfoValueText>
                  {formatContractInfoValue(location)}
                </ContractInfoValueText>
              </ContractInfoInlineField>
            </View>
            <View style={styles.pairRight}>
              <ContractInfoInlineField
                label="적정분양가"
                labelWidth={LABEL_WIDTH}
              >
                <ContractInfoValueText>
                  {formatContractInfoValue(appropriateSalePrice)}
                </ContractInfoValueText>
              </ContractInfoInlineField>
            </View>
          </View>

          {/* 행3: 시장현황(좌) | 수익성표(우) — 마지막 행, 하단 구분선 없음 */}
          <View
            style={[
              styles.pairRow,
              styles.pairRowLast,
              { minHeight: "29.5mm" },
            ]}
          >
            <View style={styles.pairLeft}>
              <ContractInfoInlineField
                label="시장현황"
                labelWidth={LABEL_WIDTH}
              >
                <ContractInfoValueText>
                  {formatContractInfoValue(marketStatus)}
                </ContractInfoValueText>
              </ContractInfoInlineField>
            </View>
            <View style={styles.pairRight}>
              <ContractInfoProfitabilityTable documentData={documentData} />
            </View>
          </View>
        </ContractInfoBorderBox>

        {isShortReviewResult ? (
          <View style={styles.shortReviewResultRow}>
            <View style={styles.reviewResultLabelWrap}>
              <ContractInfoValueText style={{ fontWeight: 700 }}>
                검토 결과
              </ContractInfoValueText>
            </View>
            <View style={styles.shortReviewResultContent}>
              <ContractInfoValueText style={styles.shortReviewResultText}>
                {formattedReviewResult}
              </ContractInfoValueText>
            </View>
          </View>
        ) : (
          <View style={styles.longReviewResultList}>
            {/* 줄 단위 행으로 page1→page2 흐름 (갈색 띠 이어짐).
                '검토 결과' 라벨은 전체 본문 1/2 지점(가운데 줄)에만 표시 */}
            {reviewResultLines.map((line, index) => (
              <View
                key={`${index}-${line}`}
                style={styles.longReviewResultLineRow}
                wrap={false}
              >
                <View style={styles.longReviewResultLabelCell}>
                  {index === reviewLabelLineIndex && (
                    <ContractInfoValueText style={{ fontWeight: 700 }}>
                      검토 결과
                    </ContractInfoValueText>
                  )}
                </View>
                <View style={styles.longReviewResultLineContent}>
                  <ContractInfoValueText style={styles.reviewResultText}>
                    {line || " "}
                  </ContractInfoValueText>
                </View>
              </View>
            ))}
          </View>
        )}

        <ContractInfoDivider />

        <ContractInfoRow borderBottom={false} minHeight="6mm">
          <ContractInfoInlineField label="처리결과" labelWidth={LABEL_WIDTH}>
            <View style={styles.processingList}>
              {FIELD.PROCESSING_RESULT.options.map((type) => (
                <DocumentDetail.CheckBox
                  key={type}
                  checked={type === processingResult}
                >
                  <ContractInfoValueText>{type}</ContractInfoValueText>
                </DocumentDetail.CheckBox>
              ))}
            </View>
          </ContractInfoInlineField>
        </ContractInfoRow>
      </View>
    </ContractInfoSection>
  );
};

export default ContractInfoReviewResultSection;
